import {
  LogLevel,
  Configuration,
  BrowserCacheLocation,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '@env/environment';

export const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1A_SIGNIN_ONLY_ROLES',
    editProfile: 'B2C_1_PasswordReset',
  },
  authorities: {
    signUpSignIn: {
      authority: environment.AadSignupSigninUrl,
    },
    editProfile: {
      authority: environment.AadEditprofileUrl,
    },
  },
  authorityDomain: environment.AadAuthorityDomain,
};

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.AadClientId,
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: `${window.location.origin}/authentication/callback`,
    postLogoutRedirectUri: `${window.location.origin}/`,
    // msal-browser v5: navigateToLoginRequestUrl und storeAuthStateInCookie (IE)
    // wurden entfernt. Verhalten nach Login-Redirect beim B2C-Login-Test pruefen.
  },
  cache: {
    // sessionStorage statt localStorage (#29547): msal-browser v5 verschluesselt
    // den localStorage-Cache und legt den Schluessel in einem Cookie
    // (msal.cache.encryption) mit JSON-Wert ab — die WAF am Application Gateway
    // blockt solche Cookies und damit die gesamte Site (403). sessionStorage
    // wird nicht verschluesselt (kein Cookie); nach Browser-Neustart/in neuen
    // Tabs greift der stille Re-Login ueber den B2C-SSO-Cookie.
    cacheLocation: BrowserCacheLocation.SessionStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: (_logLevel, _message, _containsPii) => {
        // Bei Auth-Problemen zur Diagnose einschalten:
        // console.log('[msal-lib]', _message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

// Gemeinsame, VOR dem Angular-Bootstrap initialisierte Instanz (siehe main.ts).
// msal-browser v5 wirft bei jedem Zugriff vor initialize()
// (uninitialized_public_client_application); v3 tolerierte das. Diverse Stellen
// im Code nutzen instance.getAllAccounts()/getActiveAccount() direkt.
export const msalInstance = new PublicClientApplication(msalConfig);

export const protectedResources = {
  getIncident: {
    endpoint: '/api/Incident/GetIncidents',
    scopes: [environment.AadScope],
  },
  getIncidentsAuthenticated: {
    endpoint: '/api/Incident/GetIncidentsAuthenticated',
    scopes: [environment.AadScope],
  },
  getIncidentAuthenticated: {
    endpoint: '/api/Incident/GetIncidentAuthenticated',
    scopes: [environment.AadScope],
  },
  getWorkorders: {
    endpoint: '/api/Workorder/GetWorkorders',
    scopes: [environment.AadScope],
  },
  getWorkorder: {
    endpoint: '/api/Workorder/GetWorkorder',
    scopes: [environment.AadScope],
  },
  getQuotes: {
    endpoint: '/api/Quote/GetQuotes',
    scopes: [environment.AadScope],
  },
  getQuote: {
    endpoint: '/api/Quote/GetQuote',
    scopes: [environment.AadScope],
  },
  changeQuoteState: {
    endpoint: '/api/Quote/ChangeQuoteState',
    scopes: [environment.AadScope],
  },
  getLocationFilterOptionSet: {
    endpoint: '/api/OptionSet/GetLocationFilter',
    scopes: [environment.AadScope],
  },
  getOptionSetByString: {
    endpoint: '/api/OptionSet/GetOptionSetByString',
    scopes: [environment.AadScope],
  },
  searchArchobjForAutocomplete: {
    endpoint: '/api/Location/SearchArchobjForAutocomplete',
    scopes: [environment.AadScope],
  },
  searchArchobjForAutocompleteInexact: {
    endpoint: '/api/Location/SearchArchobjForAutoCompleteInexact',
    scopes: [environment.AadScope],
  },
  postUpdateLocationInformation: {
    endpoint: '/api/Location/PostUpdateLocationInformation',
    scopes: [environment.AadScope],
  },
  postUpdateLocationHints: {
    endpoint: '/api/Location/PostUpdateLocationHints',
    scopes: [environment.AadScope],
  },
  getArchobjById: {
    endpoint: '/api/Location/GetArchobjById',
    scopes: [environment.AadScope],
  },
  getArchobjOfCurrentUser: {
    endpoint: '/api/Location/GetArchobjOfCurrentUser',
    scopes: [environment.AadScope],
  },
  getHolidayList: {
    endpoint: '/api/OptionSet/GetHolidayList',
    scopes: [environment.AadScope],
  },
  createIncidentAuthenticated: {
    endpoint: '/api/Incident/CreateIncidentAuthenticated',
    scopes: [environment.AadScope],
  },
  createPasmIncidentAuthenticated: {
    endpoint: '/api/Incident/CreatePasmIncidentAuthenticated',
    scopes: [environment.AadScope],
  },
  getTasks: {
    endpoint: '/api/Task/GetTasks',
    scopes: [environment.AadScope],
  },
  getTask: {
    endpoint: '/api/Task/GetTask',
    scopes: [environment.AadScope],
  },
  updateTask: {
    endpoint: '/api/Task/UpdateTask',
    scopes: [environment.AadScope],
  },
  getUserProfile: {
    endpoint: '/api/User/GetUserProfile',
    scopes: [environment.AadScope],
  },
  getUserEmail: {
    endpoint: '/api/User/GetUserEmail',
    scopes: [environment.AadScope],
  },
  updateUserProfile: {
    endpoint: '/api/User/UpdateUserProfile',
    scopes: [environment.AadScope],
  },
  getRentalContractForUser: {
    endpoint: '/api/User/GetRentalContractForUser',
    scopes: [environment.AadScope],
  },
  readUser: {
    endpoint: '/api/User/ReadUser',
    scopes: [environment.AadScope],
  },
  updateUser: {
    endpoint: '/api/User/UpdateUser',
    scopes: [environment.AadScope],
  },
  deleteUser: {
    endpoint: '/api/User/DeleteUser',
    scopes: [environment.AadScope],
  },
  readUsers: {
    endpoint: '/api/User/ReadUsers',
    scopes: [environment.AadScope],
  },
  readUserGroups: {
    endpoint: '/api/User/ReadUserGroups',
    scopes: [environment.AadScope],
  },
  readGroups: {
    endpoint: '/api/User/ReadGroups',
    scopes: [environment.AadScope],
  },
  addUserToGroups: {
    endpoint: '/api/User/AddUserToGroups',
    scopes: [environment.AadScope],
  },
  removeUserFromGroups: {
    endpoint: '/api/User/RemoveUserFromGroups',
    scopes: [environment.AadScope],
  },
  requestRentalcontractTermination: {
    endpoint: '/api/Incident/RequestRentalcontractTermination',
    scopes: [environment.AadScope],
  },
  getCustomizableTextList: {
    endpoint: '/api/CustomizableText/GetCustomizableTextList',
    scopes: [environment.AadScope],
  },
  getCustomizableText: {
    endpoint: '/api/CustomizableText/GetCustomizableText',
    scopes: [environment.AadScope],
  },
  updateCustomizableText: {
    endpoint: '/api/CustomizableText/UpdateCustomizableText',
    scopes: [environment.AadScope],
  },
  requestRoles: {
    endpoint: '/api/User/RequestRoles',
    scopes: [environment.AadScope],
  },
  EquipmenmtClarification: {
    endpoint: '/api/EquipmenmtClarification',
    scopes: [environment.AadScope],
  },
  RequestReasonEnums: {
    endpoint: '/api/EquipmenmtClarification/RequestReasonEnums',
    scopes: [environment.AadScope],
  },
  TechnicTypeEnums: {
    endpoint: '/api/EquipmenmtClarification/TechnicTypeEnums',
    scopes: [environment.AadScope],
  },
  CurrentRepresentative: {
    endpoint: '/api/Representative/current-representative',
    scopes: [environment.AadScope],
  },
  PossibleRepresentative: {
    endpoint: '/api/Representative/possible-representatives',
    scopes: [environment.AadScope],
  },
  RepresentativeAPI: {
    endpoint: '/api/Representative*',
    scopes: [environment.AadScope],
  },
  api: {
    endpoint: '/formulare*',
    scopes: [environment.AadScope],
  },
};

export const loginRequest = {
  scopes: [],
};

export function GetProtectedResourcesMap(): Map<string, string[]> {
  const map: Map<string, string[]> = new Map([]);

  // msal-angular v6 matcht die protectedResourceMap strikt inkl. Host:
  // relative Eintraege decken nur Same-Origin-Calls ab (devlocal-Proxy).
  // Deployed laeuft die API auf einer eigenen Subdomain (environment.baseUrl)
  // -> Endpoints zusaetzlich absolut registrieren, sonst haengt der
  // Interceptor keinen Token an (401).
  const registerEndpoint = (endpoint: string, scopes: string[]) => {
    map.set(endpoint, scopes);
    if (environment.baseUrl) {
      map.set(environment.baseUrl + endpoint, scopes);
    }
  };

  Object.keys(protectedResources).forEach(element => {
    const ressource: { endpoint: string; scopes: string[] } = protectedResources[element];
    registerEndpoint(ressource.endpoint, ressource.scopes);
  });

  if (environment.name === 'pasm') {
    registerEndpoint('/api/File/DownloadFile', [environment.AadScope]);
  }

  return map;
}
